import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AppNav } from '@/components/AppNav';
import { GhostOverlay } from '@/components/GhostOverlay';
import { fetchToday, type Photo, type PhotoStatus } from '@/lib/api';
import { encodeFromFile, encodeFromVideo } from '@/lib/encode';
import { uploadPhoto } from '@/lib/upload';
import { useCamera } from '@/hooks/useCamera';
import { useFaceAlignment } from '@/hooks/useFaceAlignment';
import { useLogout, useSession } from '@/hooks/useSession';
import { useT } from '@/i18n/context';
import { TranslatableError, translateError } from '@/i18n/errors';
import type { MessageKey, Translate } from '@/i18n/types';

/**
 * The one screen the habit actually runs through.
 *
 * The camera is only mounted when today is still free: once a photo exists the
 * limit is a product decision, not a technicality, so the screen shows the
 * photo instead of quietly letting the user shoot again and fail on submit.
 */
export function CapturePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // RequireAuth guarantees a session before this renders, so a failure here is
  // a real problem rather than the ordinary signed-out case.
  const { data, isPending, error } = useQuery({
    queryKey: ['photos', 'today'],
    queryFn: fetchToday,
  });

  const { data: user } = useSession();
  const signOut = useLogout();

  // Destructured rather than kept as one object: the lint rule that guards
  // against reading refs during render cannot tell which properties of a hook
  // result are refs, and flags the plain state values alongside them.
  const { videoRef, status: cameraStatus, error: cameraError, start, stop } = useCamera();

  const todaysPhoto = data?.photo ?? null;
  const ghost = data?.previous ?? null;
  // Never open the camera speculatively: without a session the upload can only
  // fail, and asking for the camera first would be the rudest possible way to
  // find that out.
  const needsCapture = !isPending && !error && todaysPhoto === null;

  useEffect(() => {
    if (needsCapture) {
      start();
    } else {
      stop();
    }
  }, [needsCapture, start, stop]);

  const previewLive = needsCapture && cameraStatus === 'ready';
  const { status: alignmentStatus, reading, track } = useFaceAlignment(previewLive);

  useEffect(() => {
    if (!previewLive || alignmentStatus !== 'ready') {
      return;
    }

    // Detection costs about 5ms a frame, so this is not about saving the CPU —
    // it is that the reading only feeds the hint and the final capture, and a
    // person lining their face up does not move at sixty frames a second.
    const timer = setInterval(() => {
      const video = videoRef.current;

      if (video) {
        track(video);
      }
    }, 120);

    return () => clearInterval(timer);
  }, [previewLive, alignmentStatus, track, videoRef]);

  const upload = useMutation({
    mutationFn: async (source: { kind: 'camera' } | { kind: 'file'; file: File }) => {
      // A gallery import is a still frame the tracker never saw, so it goes in
      // unaligned rather than borrowing the live preview's eye positions.
      const eyes = source.kind === 'file' ? null : (reading?.eyes ?? null);

      const encoded =
        source.kind === 'file'
          ? await encodeFromFile(source.file)
          : await encodeFromVideo(videoOrThrow(videoRef.current), eyes);

      return uploadPhoto(encoded, {
        fromGallery: source.kind === 'file',
        eyes,
        score: reading?.score ?? 0,
      });
    },
    onSuccess: (photo: Photo) => {
      stop();
      setFailure(null);
      queryClient.setQueryData(['photos', 'today'], {
        date: photo.date,
        photo,
        previous: ghost,
      });
      // Derivatives are produced by a worker, so the signed URLs only appear a
      // moment later; a short refetch is cheaper than polling forever.
      void queryClient.invalidateQueries({ queryKey: ['photos', 'today'] });
    },
    onError: (error: Error) =>
      setFailure(translateError(t, error, 'capture.upload_failed')),
  });

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6 pb-24">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Selflapse</h1>
          <p className="text-sm opacity-60">
            {data?.date ?? user?.today ?? t('capture.today')}
            {user &&
              user.streak.current > 0 &&
              ` · ${t('capture.streak', { n: user.streak.current })}`}
          </p>
        </div>

        <button
          type="button"
          // Navigating explicitly rather than waiting for the route guard to
          // notice: signing out should land on the sign-in screen, not wherever
          // the cache happens to settle a render later.
          onClick={() =>
            signOut.mutate(undefined, {
              onSettled: () => void navigate('/login', { replace: true }),
            })
          }
          disabled={signOut.isPending}
          className="text-sm underline opacity-60 disabled:opacity-30"
        >
          {t('common.sign_out')}
        </button>
      </header>

      {isPending && <p className="text-sm opacity-60">{t('capture.checking_today')}</p>}

      {error && (
        <p className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {t('common.api_unreachable', { message: error.message })}
        </p>
      )}

      {todaysPhoto && <TodaysPhoto photo={todaysPhoto} />}

      {needsCapture && (
        <section className="flex flex-col gap-4">
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-current/5">
            <video
              ref={videoRef}
              playsInline
              muted
              // Mirrored for the preview only: an unmirrored self-view feels
              // wrong to look at, while the stored frame stays as shot.
              className="size-full -scale-x-100 object-cover"
            />

            {previewLive && ghost?.url && <GhostOverlay src={ghost.url} />}

            {cameraStatus !== 'ready' && (
              <p className="absolute inset-0 grid place-items-center p-6 text-center text-sm opacity-70">
                {cameraError ? t(cameraError) : t('capture.opening_camera')}
              </p>
            )}
          </div>

          {/* Only meaningful over a live preview. Without a camera the tracker
              never starts, so an unconditional hint would sit on "Loading face
              alignment…" forever, blaming the wrong thing. */}
          {previewLive && (
            <p className="text-center text-sm opacity-60">
              {alignmentHint(t, alignmentStatus, reading?.score ?? null, Boolean(ghost))}
            </p>
          )}

          <button
            type="button"
            disabled={cameraStatus !== 'ready' || upload.isPending}
            onClick={() => upload.mutate({ kind: 'camera' })}
            className="rounded-full bg-current/10 px-6 py-4 font-medium disabled:opacity-40"
          >
            {upload.isPending ? t('capture.uploading') : t('capture.shutter')}
          </button>

          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => fileInputRef.current?.click()}
            className="text-sm underline opacity-60 disabled:opacity-30"
          >
            {t('capture.gallery')}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Reset so picking the same file twice still fires a change.
              event.target.value = '';

              if (file) {
                upload.mutate({ kind: 'file', file });
              }
            }}
          />
        </section>
      )}

      {failure && (
        <p className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">{failure}</p>
      )}

      <AppNav />
    </main>
  );
}

/** A `Record` so adding a status to the API makes the compiler ask for a word. */
const STATUS_KEYS: Record<PhotoStatus, MessageKey> = {
  pending: 'capture.status_pending',
  processing: 'capture.status_processing',
  ready: 'capture.status_ready',
  failed: 'capture.status_failed',
};

function TodaysPhoto({ photo }: { photo: Photo }) {
  const t = useT();

  return (
    <section className="flex flex-col gap-3">
      <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-current/5">
        {photo.url ? (
          <img
            src={photo.url}
            alt={t('capture.photo_alt')}
            className="size-full object-cover"
            loading="lazy"
          />
        ) : (
          <p className="grid size-full place-items-center text-sm opacity-60">
            {photo.status === 'failed'
              ? t('capture.upload_failed')
              : t('capture.processing')}
          </p>
        )}
      </div>

      <p className="text-sm opacity-60">
        {photo.status === 'ready'
          ? t('capture.done')
          : t('capture.status', { status: t(STATUS_KEYS[photo.status]) })}
      </p>
    </section>
  );
}

/**
 * One line of guidance under the shutter.
 *
 * Says nothing at all when the face is already well placed: a hint that is
 * always on screen stops being read.
 */
function alignmentHint(
  t: Translate,
  status: 'loading' | 'ready' | 'unavailable',
  score: number | null,
  hasGhost: boolean,
): string {
  const key = hintKey(status, score, hasGhost);

  return key ? t(key) : '';
}

function hintKey(
  status: 'loading' | 'ready' | 'unavailable',
  score: number | null,
  hasGhost: boolean,
): MessageKey | null {
  if (status === 'unavailable') {
    // Not an error the user can act on, so it is phrased as a limitation.
    return 'capture.hint_unavailable';
  }

  if (status === 'loading') {
    return 'capture.hint_loading';
  }

  if (score === null) {
    return hasGhost ? 'capture.hint_outline' : 'capture.hint_searching';
  }

  if (score < 0.5) {
    return 'capture.hint_closer';
  }

  return null;
}

function videoOrThrow(video: HTMLVideoElement | null): HTMLVideoElement {
  if (!video) {
    throw new TranslatableError('errors.camera_not_ready');
  }

  return video;
}
